// components/tier-branch/useSnack.tsx
// Tiny shared snackbar helper for the Tier & Branch pages, matching the
// MUI Snackbar/Alert feedback pattern used across admin-management tabs.
import React, { useCallback, useState } from "react";
import { Snackbar, Alert, AlertColor } from "@mui/material";

export type Notify = (msg: string, severity?: AlertColor) => void;

interface SnackState {
  open: boolean;
  msg: string;
  severity: AlertColor;
}

export function useSnack(): { notify: Notify; SnackBar: React.ReactElement } {
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    msg: "",
    severity: "success",
  });

  const notify = useCallback<Notify>((msg, severity = "success") => {
    setSnack({ open: true, msg, severity });
  }, []);

  const SnackBar = (
    <Snackbar
      open={snack.open}
      autoHideDuration={4000}
      onClose={() => setSnack((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert
        severity={snack.severity}
        variant="filled"
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        {snack.msg}
      </Alert>
    </Snackbar>
  );

  return { notify, SnackBar };
}

/** Format a number as a localized amount, or em-dash when empty. */
export const fmt = (n: number | null | undefined) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
